import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { API_BASE_URL } from '@app/core/tokens/api-base-url.token';
import { SnackbarService } from '@app/core/services/snackbar.service';
import {
  CreateTypeOfDamageRequest,
  TypeOfDamageModel,
} from '@app/core/models/product-parameters/type-of-damage.model';

@Injectable({
  providedIn: 'root',
})
export class TypeOfDamageService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_URL);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translate = inject(TranslateService);

  readonly isLoading = signal(false);

  private get baseUrl(): string {
    return `${this.apiBaseUrl}/b2b/type-of-damages`;
  }

  getAll(): Observable<TypeOfDamageModel[]> {
    this.isLoading.set(true);
    return this.http.get<TypeOfDamageModel[]>(this.baseUrl).pipe(
      catchError(error => {
        console.error('Ошибка при получении типов повреждений:', error);
        this.translate.get('Ошибка при получении типов повреждений').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        return of([]);
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  getActive(): Observable<TypeOfDamageModel[]> {
    this.isLoading.set(true);
    return this.http.get<TypeOfDamageModel[]>(`${this.baseUrl}/active`).pipe(
      catchError(error => {
        console.error('Ошибка при получении активных типов повреждений:', error);
        this.translate.get('Ошибка при получении типов повреждений').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        return of([]);
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  create(body: CreateTypeOfDamageRequest): Observable<TypeOfDamageModel> {
    this.isLoading.set(true);
    return this.http.post<TypeOfDamageModel>(this.baseUrl, body).pipe(
      catchError(error => {
        console.error('Ошибка при создании типа повреждения:', error);
        this.translate.get('Ошибка при создании типа повреждения').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  update(id: string, body: CreateTypeOfDamageRequest): Observable<TypeOfDamageModel> {
    this.isLoading.set(true);
    return this.http.put<TypeOfDamageModel>(`${this.baseUrl}/${id}`, body).pipe(
      catchError(error => {
        console.error('Ошибка при обновлении типа повреждения:', error);
        this.translate.get('Ошибка при обновлении типа повреждения').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  delete(id: string): Observable<void> {
    this.isLoading.set(true);
    return this.http.delete<void>(`${this.baseUrl}/${id}`).pipe(
      catchError(error => {
        console.error('Ошибка при удалении типа повреждения:', error);
        this.translate.get('Ошибка при удалении типа повреждения').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }
}
